import { useQueryData } from "@src/hooks/useQueryData";
import { byCode, reloadAndReplaceCachedListItem } from "@src/hooks/queryListCache";
import {
    actOfCheckingAvailabilityServices,
    type ActOfCheckingAvailability,
    type ActOfCheckingAvailabilityContent,
    type ActOfCheckingAvailabilityContentExtends,
    type ActOfCheckingAvailabilityExtends,
} from "../../../../api/actOfCheckingAvailability";

const PARTIAL_INVENTORY_CARD_INCLUDES = {
    include: ["Division", "State"] satisfies Array<keyof ActOfCheckingAvailabilityExtends>,
};

/** Include-набор связей строки АПН для карточки частичной инвентаризации. */
export const PARTIAL_INVENTORY_CONTENT_INCLUDES = {
    include: ["DepotCard", "StockObj", "Measure", "Lot", "Order", "PlacedCellRemainders"] satisfies Array<
        keyof ActOfCheckingAvailabilityContentExtends
    >,
};

/** Загружает карточку АПН для частичной инвентаризации склада. */
export function useStockPartialInventoryCardQuery(actCode: number) {
    return useQueryData<ActOfCheckingAvailability | null>({
        queryKey: ["stock", "partialInventory", actCode, "card"],
        queryFn: ({ signal }) =>
            actOfCheckingAvailabilityServices.loadByCode(
                {
                    code: actCode,
                    ...PARTIAL_INVENTORY_CARD_INCLUDES,
                },
                { signal },
            ),
        enabled: actCode > 0,
        meta: { errorSource: "stock.partialInventory.card" },
    });
}

/** Результат загрузки строк АПН для частичной инвентаризации склада. */
export interface UseStockPartialInventoryContentsQueryResult {
    /** Строки состава АПН. */
    content: ActOfCheckingAvailabilityContent[];
    /** Признак фоновой загрузки. */
    isFetching: boolean;
    /** Перезагружает строки состава АПН. */
    refetch: () => Promise<unknown>;
    /**
     * Перезагружает одну строку состава АПН и заменяет её в query cache.
     * @param contentCode код строки состава АПН
     */
    refetchContentByCode: (contentCode: number) => Promise<void>;
    /**
     * Вставляет или заменяет строку состава в query cache.
     * @param content строка состава АПН
     */
    upsertContent: (content: ActOfCheckingAvailabilityContent) => void;
    /** Актуальный снимок состава из query cache (не реактивный). */
    getContentSnapshot: () => ActOfCheckingAvailabilityContent[];
}

/** Загружает строки АПН для частичной инвентаризации склада. */
export function useStockPartialInventoryContentsQuery(actCode: number): UseStockPartialInventoryContentsQueryResult {
    const query = useQueryData<ActOfCheckingAvailabilityContent[]>({
        queryKey: ["stock", "partialInventory", actCode, "contents"],
        queryFn: ({ signal }) =>
            actOfCheckingAvailabilityServices.content.load(
                {
                    filter: { ActCode: actCode },
                    ...PARTIAL_INVENTORY_CONTENT_INCLUDES,
                },
                { signal },
            ),
        enabled: actCode > 0,
        meta: { errorSource: "stock.partialInventory.contents" },
    });

    const refetchContentByCode = async (contentCode: number): Promise<void> => {
        await reloadAndReplaceCachedListItem({
            setData: query.setData,
            getKey: byCode,
            loadItem: () =>
                actOfCheckingAvailabilityServices.content.loadByCode({
                    code: contentCode,
                    actCode,
                    ...PARTIAL_INVENTORY_CONTENT_INCLUDES,
                }),
        });
    };

    const upsertContent = (content: ActOfCheckingAvailabilityContent): void => {
        query.setData((prev) => {
            const list = prev ?? [];
            const index = list.findIndex((item) => item.Code === content.Code);

            if (index < 0) {
                return [...list, content];
            }

            const next = [...list];
            next[index] = content;
            return next;
        });
    };

    const getContentSnapshot = (): ActOfCheckingAvailabilityContent[] => {
        return query.getData() ?? [];
    };

    return {
        content: query.data ?? [],
        isFetching: query.isFetching,
        refetch: query.refetch,
        refetchContentByCode,
        upsertContent,
        getContentSnapshot,
    };
}
